'use client';
import { type HTMLAttributes } from 'react';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

/** A single skeleton line/block. Compose multiples for table rows, cards. */
export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={['animate-shimmer rounded-control bg-state-loading-bg', className]
        .filter(Boolean)
        .join(' ')}
      {...props}
    />
  );
}

/** Pre-composed skeleton for a table row (one Skeleton block per column cell). */
export function SkeletonTableRow({ cols = 4 }: { cols?: number }) {
  return (
    <tr aria-hidden="true">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

/** Pre-composed skeleton for N table rows. */
export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonTableRow key={i} cols={cols} />
      ))}
    </>
  );
}

/**
 * Pre-composed skeleton for a bordered card holding a label + value + one more
 * line (e.g. a KPI/hero widget still fetching). Not a real `<table>` — for
 * that, use `SkeletonTable` inside a `<table>`/`<tbody>`.
 */
export function SkeletonCard() {
  return (
    <div className="space-y-3 rounded-card border border-border bg-surface-elevated p-5">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-8 w-24" />
      <Skeleton className="h-4 w-full" />
    </div>
  );
}

/**
 * Pre-composed block-list placeholder for a list/table whose columns aren't
 * known yet (data hasn't loaded) — a header bar + N full-width row bars.
 * Rendered as a sibling before the real table mounts, not inside one; use
 * `SkeletonTable` instead when the columns are already known and the
 * placeholder lives inside `<table><tbody>`.
 */
export function SkeletonBlockRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** Pre-composed full-page splash skeleton (heading + a couple lines + a block). */
export function SkeletonPage() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}
